import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NcpbService {
  private readonly http = inject(HttpClient);
  private readonly endpoint = '/ncpb2/api';

  getCountries(): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.endpoint}/admin/ism/v2/fetch_all`
    );
  }

  getCountryJSON(countryCode: string): Observable<any> {
    return this.http.get<any>(
      `${this.endpoint}/admin/ism/fetch/json`,
      {
        params: new HttpParams().set('countryCode', countryCode),
      }
    );
  }

  patient(countryCode: string, identifiers: any[]): Observable<any> {
    const payload: any = {
      countryCode,
      patientIdentityTrait: {
        livingSubjectIds: identifiers.map((x) => ({
          extension: x.extension_value,
          root: x.domain,
        })),
      },
    };

    if (identifiers[0]?.extension_value_date) {
      payload.patientIdentityTrait.birthDate =
        identifiers[0].extension_value_date;
    }

    return this.http.post<any>(
      `${this.endpoint}/v2/patient`,
      payload
    );
  }

  ps(payload: {
    countryCode: string;
    patientIdentifier: string;
    purposeOfUse: string;
  }) {
    return this.http.post<any>(`${this.endpoint}/v2/ps`, payload);
  }

  ep(payload: {
    countryCode: string;
    patientIdentifier: string;
    purposeOfUse: string;
    prescriptionId?: string;
    dispensationPinCode?: string;
  }) {
    return this.http.post<any>(`${this.endpoint}/ep`, payload);
  }

  orcd(payload: {
    countryCode: string;
    classCodes: string[];
    patientIdentifier: string;
    purposeOfUse: string;
  }) {
    return this.http.post<any>(`${this.endpoint}/v2/orcd`, {
      countryCode: payload.countryCode,
      classCodes: payload.classCodes,
      filterParameters: {},
      patientTrait: {
        countryCode: payload.countryCode,
        patientIdentifier: payload.patientIdentifier,
        purposeOfUse: payload.purposeOfUse,
      },
    });
  }

  psRetrieve(payload: {
    countryCode: string;
    nextOfKin: boolean;
    patientIdentifier: string;
    purposeOfUse: string;
    repositoryId: string;
    documentIdentifier: string;
    homeCommunityId: string;
  }) {
    return this.http.post<any>(
      `${this.endpoint}/v2/ps/retrieve`,
      payload
    );
  }

  psRetrieveDisplay(payload: {
    countryCode: string;
    nextOfKin: boolean;
    patientIdentifier: string;
    purposeOfUse: string;
    repositoryId: string;
    documentIdentifier: string;
    homeCommunityId: string;
  }) {
    return this.http.post(
      `${this.endpoint}/v2/ps/retrieve-display`,
      payload,
      {
        responseType: 'text',
      }
    );
  }

  orcdRetrieve(payload: {
    countryCode: string;
    classCode: string;
    patientIdentifier: string;
    purposeOfUse: string;
    repositoryId: string;
    documentIdentifier: string;
    homeCommunityId: string;
  }) {
    return this.http.post<any>(
      `${this.endpoint}/v2/orcd/retrieve`,
      payload
    );
  }

  epRetrieve(
    payload: {
      countryCode: string;
      nextOfKin: boolean;
      patientIdentifier: string;
      purposeOfUse: string;
      repositoryId: string;
      documentIdentifier: string;
      homeCommunityId: string;
      prescriptionId?: string;
      dispensationPinCode?: string;
    }
  ) {
    return this.http.post<any>(
      `${this.endpoint}/v2/ep/retrieve`,
      payload
    );
  }

  epDisplay(payload: {
    countryCode: string;
    nextOfKin: boolean;
    patientIdentifier: string;
    purposeOfUse: string;
    repositoryId: string;
    documentIdentifier: string;
    homeCommunityId: string;
    clinicalDocumentContent: {
      content: string;
    };
  }) {
    return this.http.post(
      `${this.endpoint}/v2/ep/display`,
      payload,
      {
        responseType: 'text',
      }
    );
  }

  getPharmacy() {
    return this.http.get(`/ncpb/pharmacy`);
  }

  epDispense(payload: {
    countryCode: string;
    cda: string;
    homeCommunityId: string;
    repositoryId: string;
    patientIdentifier: string;
    purposeOfUse: string;
    pharmacist_id: string;
    pharmacy_id: string;
    drugid: string;
    packnr: string;
    number_of_packages: string;
    quantity: string;
    quantity_unit: string;
    frequency_of_intake: string;
    frequency_of_intake_unit: string;
    duration: string;
    duration_unit: string;
    substitution: boolean;
  }) {
    return this.http.post<any>(
      `${this.endpoint}/v2/ep/dispense`,
      payload
    );
  }

}
